import React, { startTransition, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import CustomerReviewsList from 'components/consumer/customer-reviews-list';
import CustomerReviewsHeader from 'components/consumer/customer-reviews-list/CustomerReviewHeader';
import { logError } from 'commons/logger';
import useSuspenseQuery from 'commons/useSuspenseQuery';
import { DEFAULT_SELLER_REVIEWS_SORT, GET_SELLER_REVIEWS } from './queries';
import { REVIEWS_PAGE_SEO_DESCRIPTION } from '../../../commons/constants';
const RESELLER_RATINGS_STORE_URL = 'https://www.resellerratings.com/store/Joybird';
const sellerReviewsSortOptions = [{
  value: 'highest',
  label: 'Highest Rated'
}, {
  value: 'newest',
  label: 'Newest'
}, {
  value: 'oldest',
  label: 'Oldest'
}, {
  value: 'lowest',
  label: 'Lowest Rated'
}];
const CustomerReviewsPage = ({
  config
}) => {
  const {
    type,
    title,
    subTitle
  } = config;
  const [sortBy, setSortBy] = useState(DEFAULT_SELLER_REVIEWS_SORT);
  // Pages already merged into the Apollo cache, per sort option
  const pagesLoadedBySort = useRef({});
  const isLoadingMore = useRef(false);
  const {
    data,
    fetchMore
  } = useSuspenseQuery(GET_SELLER_REVIEWS, {
    variables: {
      sortBy,
      page: 1
    }
  });
  const reviews = data?.sellerReviews?.reviews ?? [];
  const totalReviewsCount = data?.sellerReviews?.total ?? 0;
  const averageScore = data?.sellerReviews?.averageRating ?? 0;
  const hasMore = data?.sellerReviews?.hasMore ?? false;
  const loadMore = async () => {
    const pagesLoaded = pagesLoadedBySort.current[sortBy] ?? 1;
    if (isLoadingMore.current || !hasMore) {
      return;
    }
    isLoadingMore.current = true;
    const nextPage = pagesLoaded + 1;
    try {
      let request;
      // Outside a transition fetchMore re-suspends the page and blanks it
      startTransition(() => {
        request = fetchMore({
          variables: {
            sortBy,
            page: nextPage
          },
          updateQuery: (prevResults, {
            fetchMoreResult
          }) => {
            if (!fetchMoreResult) return prevResults;
            // New reviews can shift pages between requests
            const loadedIds = new Set(prevResults.sellerReviews.reviews.map(({
              id
            }) => id));
            return {
              // Keep page 1 totals, a failed page comes back with zeros
              sellerReviews: {
                ...prevResults.sellerReviews,
                hasMore: fetchMoreResult.sellerReviews.hasMore,
                reviews: [...prevResults.sellerReviews.reviews, ...fetchMoreResult.sellerReviews.reviews.filter(({
                  id: id_0
                }) => !loadedIds.has(id_0))]
              }
            };
          }
        });
      });
      const {
        data: nextPageData
      } = await request;
      if (nextPageData?.sellerReviews?.reviews?.length) {
        pagesLoadedBySort.current[sortBy] = nextPage;
      }
    } catch (err) {
      logError(err);
    } finally {
      isLoadingMore.current = false;
    }
  };
  return <>
      <Helmet>
        <meta name="description" content={REVIEWS_PAGE_SEO_DESCRIPTION} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Reviews by Joybird.com" />

        <meta property="og:description" content={REVIEWS_PAGE_SEO_DESCRIPTION} />

        <meta property="og:image" content="https://joybird2.imgix.net/meta/jb0916-FacebookPageThumbnails-Reviews-v1.jpg?auto=compress,format" />

        <meta property="og:url" content="https://joybird.com/reviews/" />
      </Helmet>

      <CustomerReviewsHeader title={title} subTitle={subTitle} type={type} averageScore={averageScore} totalReviewsCount={totalReviewsCount} sortOptions={sellerReviewsSortOptions} onSortChanged={selectedOption => {
      startTransition(() => {
        setSortBy(selectedOption.value);
      });
    }} />
      <CustomerReviewsList config={{
      type,
      reviews,
      // Infinite scroll would keep the footer out of reach
      infiniteScroll: false,
      loadMore,
      hasMore,
      totalReviewsCount
    }} />
      {totalReviewsCount > 0 ? <p className="text-sm text-gray-light1 text-center mt-0 mx-[3.333vw] mb-8">
          Reviews powered by{' '}
          <a href={RESELLER_RATINGS_STORE_URL} target="_blank" rel="noopener noreferrer" className="underline text-gray">
            ResellerRatings
          </a>
        </p> : null}
    </>;
};
export default CustomerReviewsPage;